import { useNavigate } from 'react-router-dom';
import loading from '/asset/loading.gif';
import { ResetPasswordCredentials } from '../interfaces';
import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { clearError } from '../store/purchaseSlice';
import CodeInput from '../components/CodeInput';
import { resetGoToReset, tokenResetPasswordAsync } from '../store/authSlice';
import { toast } from 'react-toastify';
import EyeIcon from '/asset/eye.svg';
import ClosedEyeIcon from '/asset/closed-eye.svg';
import AuthCard, {
  authButtonClass,
  authInputClass,
  authLabelClass,
} from '../components/AuthCard';

export default function CreatePassword() {
  const navigate = useNavigate();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [showPassword, setShowPassword] = useState(false);
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!-/:-@[-`{-~])[!-~]{8,}$/;

  const dispatch = useDispatch<AppDispatch>();
  const { status: auth_status, email } = useSelector(
    (state: RootState) => state.auth
  );
  const { routesPrefix } = useSelector((state: RootState) => state.setup);
  const [validData, setValidData] = useState<boolean>(false);
  const [resetPasswordData, setResetPasswordData] =
    useState<ResetPasswordCredentials>({
      email: '',
      password: '',
      confirmPassword: '',
      token: '',
    });

  const [resetPasswordValidDetails, setResetPasswordValidDetails] =
    useState<ResetPasswordCredentials>({
      email: '',
      password: '',
      confirmPassword: '',
      token: '',
    });

  const instructionsFormat = (password: string): JSX.Element => {
    const requirements = [
      {
        label: '8 characters',
        test: (pw: string) => pw.length >= 8,
      },
      {
        label: 'at least one letter',
        test: (pw: string) => /[a-zA-Z]/.test(pw),
      },
      {
        label: 'at least one digit',
        test: (pw: string) => /\d/.test(pw),
      },
      {
        label: 'at least one special character',
        test: (pw: string) => /[!-/:-@[-`{-~]/.test(pw),
      },
    ];

    return (
      <div className="w-full font-dmsans text-[12px] text-gray-500">
        <p className="mb-[4px]">Your password must contain:</p>
        <div className="flex flex-col gap-[2px]">
          {requirements.map((requirement, index) => {
            const testresult = requirement.test(password);
            return (
              <div
                key={index}
                className={testresult ? 'text-green-600' : 'text-red-500'}
              >
                {testresult ? '✅' : '❌'} {requirement.label}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  useEffect(() => {
    let valid: boolean = true;
    const rpvd = {
      email: '',
      password: '',
      confirmPassword: '',
      token: '',
    };

    if (!emailRegex.test(resetPasswordData.email)) {
      rpvd.email = 'Use a valid email address';
      valid = false && valid;
    } else {
      rpvd.email = '';
    }

    if (!passwordRegex.test(resetPasswordData.password)) {
      rpvd.password = `Use a valid password. i.e.
      #/-8 characters-/
      /-at least one letter-/
      /-at least one digit-/
      /-at least one special character from @$!%*?&- -/#`;
      valid = false && valid;
    } else {
      rpvd.password = '';
    }

    if (String(resetPasswordData.token).length !== 6) {
      rpvd.token = 'Token should be 6 Characters';
      valid = false && valid;
    } else {
      rpvd.token = '';
    }

    if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
      rpvd.confirmPassword = 'Confirm Password does not match password';
      valid = false && valid;
    } else {
      rpvd.confirmPassword = '';
    }

    setResetPasswordValidDetails(rpvd);
    setValidData(valid);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPasswordData]);

  const updateResetPasswordData = (name: string, value: string) => {
    setResetPasswordData({ ...resetPasswordData, [name]: value });
  };

  useEffect(() => {
    dispatch(resetGoToReset());
  }, [dispatch]);

  useEffect(() => {
    setResetPasswordData({ ...resetPasswordData, email: email });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handleSubmit = () => {
    if (validData) {
      dispatch(tokenResetPasswordAsync(resetPasswordData)).then((res) => {
        if (res.payload && res.payload.succeded) {
          navigate(`${routesPrefix}auth`);
        }
        dispatch(clearError());
      });
    } else {
      toast.error('Please fill all the fields correctly', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
      });
    }
  };

  const fieldError = (message: ReactNode) => (
    <div className="text-[12px] text-red-500 mt-[6px] font-dmsans">
      {message}
    </div>
  );

  return (
    <AuthCard
      title="Verify your email"
      subtitle={
        <>
          We sent a 6-digit code to{' '}
          {emailRegex.test(email) ? (
            <span className="font-medium text-gray-700">{email}</span>
          ) : (
            'your email address'
          )}
          .<br />
          Enter it below and set your password.
        </>
      }
      icon={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-[24px] h-[24px]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path strokeLinecap="round" strokeLinejoin="round" d="m3 7 9 6 9-6" />
        </svg>
      }
    >
      {/* Token */}
      <div className="mb-[24px]">
        <CodeInput
          fields={6}
          inputMode={'numeric'}
          onChange={(value) => updateResetPasswordData('token', value)}
          value={resetPasswordData.token}
        />
        {resetPasswordValidDetails.token !== '' && (
          <div className="text-center">
            {fieldError(resetPasswordValidDetails.token)}
          </div>
        )}
      </div>

      {/* Email (only when not already known) */}
      {!emailRegex.test(email) && (
        <div className="mb-[22px]">
          <label className={authLabelClass}>Email Address</label>
          <input
            className={authInputClass}
            placeholder="you@example.com"
            type="text"
            value={resetPasswordData.email}
            onChange={(e) => updateResetPasswordData('email', e.target.value)}
          />
          {resetPasswordValidDetails.email !== '' &&
            fieldError(resetPasswordValidDetails.email)}
        </div>
      )}

      {/* Password */}
      <div className="mb-[22px]">
        <label className={authLabelClass}>Password</label>
        <div className="relative">
          <input
            className={`${authInputClass} pr-[48px]`}
            placeholder="Create a strong password"
            type={showPassword ? 'text' : 'password'}
            value={resetPasswordData.password}
            onChange={(e) =>
              updateResetPasswordData('password', e.target.value)
            }
          />
          <img
            src={!showPassword ? ClosedEyeIcon : EyeIcon}
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-[16px] top-1/2 -translate-y-1/2 cursor-pointer w-[20px] h-[20px] object-contain opacity-60 hover:opacity-90 transition-opacity"
          />
        </div>
        {resetPasswordValidDetails.password !== '' && (
          <div className="mt-[8px]">
            {instructionsFormat(resetPasswordData.password)}
          </div>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label className={authLabelClass}>Confirm Password</label>
        <div className="relative">
          <input
            className={`${authInputClass} pr-[48px]`}
            placeholder="Re-enter your password"
            type={showPassword ? 'text' : 'password'}
            value={resetPasswordData.confirmPassword}
            onChange={(e) =>
              updateResetPasswordData('confirmPassword', e.target.value)
            }
          />
          <img
            src={!showPassword ? ClosedEyeIcon : EyeIcon}
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-[16px] top-1/2 -translate-y-1/2 cursor-pointer w-[20px] h-[20px] object-contain opacity-60 hover:opacity-90 transition-opacity"
          />
        </div>
        {resetPasswordValidDetails.confirmPassword !== '' &&
          fieldError(resetPasswordValidDetails.confirmPassword)}
      </div>

      {/* Submit */}
      {auth_status !== 'loading' ? (
        <button
          className={`${authButtonClass} mt-[28px] text-white ${
            validData
              ? 'bg-[#1E6DB4] hover:bg-[#195d9a] active:bg-[#154f83] cursor-pointer'
              : 'bg-[#1E6DB4]/40 shadow-none cursor-not-allowed'
          }`}
          onClick={handleSubmit}
        >
          Confirm Token
        </button>
      ) : (
        <div className="flex justify-center items-center gap-2 h-[52px] mt-[28px]">
          <img src={loading} className="w-[32px] h-[20px] object-cover" />
          <span className="font-dmsans text-[13px] text-gray-500">
            Loading...
          </span>
        </div>
      )}

      <div className="mt-[24px] text-center font-dmsans text-[14px] text-gray-500">
        Didn't receive it?{' '}
        <span
          className="text-[#1E6DB4] cursor-pointer hover:underline font-medium"
          onClick={() => navigate(`${routesPrefix}resetpassword`)}
        >
          Resend code
        </span>
      </div>
    </AuthCard>
  );
}
